import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {LoadSecureDocumentResult, SecureViewerDocumentRef, SecureViewerFitBounds} from './types'
import {renderImageWithWatermark, renderPdfAsImages, resolveSecureDocumentKind} from './utils'

export type UseSecureDocumentViewerOptions<TDocument extends SecureViewerDocumentRef> = {
  acquirePreviewSession: () => Promise<{previewToken: string; expiresAtUtc?: string}>
  refreshPreviewSession: (
    currentPreviewToken: string | null
  ) => Promise<{previewToken: string; expiresAtUtc?: string}>
  loadDocument: (previewToken: string, document: TDocument) => Promise<LoadSecureDocumentResult>
  minPdfWidthPx?: number
  maxPdfWidthPx?: number
  viewportPaddingPx?: number
  maxPdfPages?: number
  minZoom?: number
  maxZoom?: number
  zoomStep?: number
  watermarkText?: string | null | ((document: TDocument) => string | null | undefined)
}

const DEFAULT_OPTIONS = {
  minPdfWidthPx: 360,
  maxPdfWidthPx: 1600,
  viewportPaddingPx: 48,
  maxPdfPages: 20,
  minZoom: 0.5,
  maxZoom: 2.5,
  zoomStep: 0.25,
}

type ViewerStatus = 'idle' | 'loading' | 'ready' | 'expired' | 'unavailable'

const PREVIEW_SESSION_EXPIRED_CODE = 'PREVIEW_SESSION_EXPIRED'

const readErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null
  }

  const candidate = (error as {code?: unknown}).code
  if (typeof candidate === 'string' && candidate.trim()) {
    return candidate.trim()
  }

  return null
}

const readErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim()
  }

  return null
}

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value))

export const useSecureDocumentViewer = <TDocument extends SecureViewerDocumentRef>(
  options: UseSecureDocumentViewerOptions<TDocument>
) => {
  const {
    acquirePreviewSession,
    refreshPreviewSession,
    loadDocument,
    minPdfWidthPx = DEFAULT_OPTIONS.minPdfWidthPx,
    maxPdfWidthPx = DEFAULT_OPTIONS.maxPdfWidthPx,
    viewportPaddingPx = DEFAULT_OPTIONS.viewportPaddingPx,
    maxPdfPages = DEFAULT_OPTIONS.maxPdfPages,
    minZoom = DEFAULT_OPTIONS.minZoom,
    maxZoom = DEFAULT_OPTIONS.maxZoom,
    zoomStep = DEFAULT_OPTIONS.zoomStep,
    watermarkText = null,
  } = options
  const mergedOptions = useMemo(
    () => ({
      acquirePreviewSession,
      refreshPreviewSession,
      loadDocument,
      minPdfWidthPx,
      maxPdfWidthPx,
      viewportPaddingPx,
      maxPdfPages,
      minZoom,
      maxZoom,
      zoomStep,
      watermarkText,
    }),
    [
      acquirePreviewSession,
      loadDocument,
      maxPdfPages,
      maxPdfWidthPx,
      maxZoom,
      minPdfWidthPx,
      minZoom,
      refreshPreviewSession,
      viewportPaddingPx,
      watermarkText,
      zoomStep,
    ]
  )

  const [viewerDocument, setViewerDocument] = useState<TDocument | null>(null)
  const [viewerObjectUrl, setViewerObjectUrl] = useState<string | null>(null)
  const [viewerContentType, setViewerContentType] = useState<string | null>(null)
  const [viewerPdfPages, setViewerPdfPages] = useState<string[]>([])
  const [viewerLoading, setViewerLoading] = useState(false)
  const [viewerError, setViewerError] = useState<string | null>(null)
  const [viewerStatus, setViewerStatus] = useState<ViewerStatus>('idle')
  const [zoomLevel, setZoomLevel] = useState(1)
  const [previewToken, setPreviewToken] = useState<string | null>(null)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [pdfSourceBlob, setPdfSourceBlob] = useState<Blob | null>(null)
  const openerElementRef = useRef<HTMLElement | null>(null)
  const renderRequestIdRef = useRef(0)

  const viewerDocumentKind = useMemo(
    () => resolveSecureDocumentKind(viewerDocument ?? undefined, viewerContentType),
    [viewerContentType, viewerDocument]
  )

  useEffect(() => {
    return () => {
      if (viewerObjectUrl) {
        URL.revokeObjectURL(viewerObjectUrl)
      }
    }
  }, [viewerObjectUrl])

  const revokeViewerObjectUrl = useCallback(() => {
    setViewerObjectUrl((currentValue) => {
      if (currentValue) {
        URL.revokeObjectURL(currentValue)
      }

      return null
    })
  }, [])

  const resolveWatermarkText = useCallback(
    (document: TDocument): string | null => {
      const resolvedText =
        typeof mergedOptions.watermarkText === 'function'
          ? mergedOptions.watermarkText(document)
          : mergedOptions.watermarkText
      const normalizedText = resolvedText?.trim()

      return normalizedText ? normalizedText : null
    },
    [mergedOptions]
  )

  const renderPdfPages = useCallback(
    async (
      blob: Blob,
      nextZoomLevel: number,
      preserveCurrentPage: boolean,
      watermarkText?: string | null
    ) => {
      const requestId = ++renderRequestIdRef.current
      setViewerLoading(true)

      try {
        const viewportWidth =
          typeof window !== 'undefined' ? window.innerWidth : mergedOptions.maxPdfWidthPx
        const baseWidth = Math.max(
          mergedOptions.minPdfWidthPx,
          Math.min(mergedOptions.maxPdfWidthPx, viewportWidth - mergedOptions.viewportPaddingPx)
        )
        const targetWidth = Math.max(220, Math.floor(baseWidth * nextZoomLevel))
        const pages = await renderPdfAsImages(
          blob,
          targetWidth,
          mergedOptions.maxPdfPages,
          watermarkText
        )

        if (requestId !== renderRequestIdRef.current) {
          return
        }

        if (pages.length === 0) {
          throw new Error('Preview unavailable')
        }

        setViewerPdfPages(pages)
        setCurrentPageIndex((previousIndex) => {
          if (!preserveCurrentPage) {
            return 0
          }

          return Math.max(0, Math.min(previousIndex, pages.length - 1))
        })
        setViewerStatus('ready')
        setViewerError(null)
      } catch (error) {
        if (requestId !== renderRequestIdRef.current) {
          return
        }

        setViewerStatus('unavailable')
        setViewerError(readErrorMessage(error) ?? 'Preview unavailable')
      } finally {
        if (requestId === renderRequestIdRef.current) {
          setViewerLoading(false)
        }
      }
    },
    [
      mergedOptions.maxPdfPages,
      mergedOptions.maxPdfWidthPx,
      mergedOptions.minPdfWidthPx,
      mergedOptions.viewportPaddingPx,
    ]
  )

  const resolvePreviewToken = useCallback(
    async (mode: 'reuse' | 'acquire' | 'refresh'): Promise<string> => {
      if (mode === 'refresh') {
        const refreshed = await mergedOptions.refreshPreviewSession(previewToken)
        setPreviewToken(refreshed.previewToken)
        return refreshed.previewToken
      }

      if (mode === 'acquire' || !previewToken) {
        const acquired = await mergedOptions.acquirePreviewSession()
        setPreviewToken(acquired.previewToken)
        return acquired.previewToken
      }

      return previewToken
    },
    [mergedOptions, previewToken]
  )

  const loadPreviewDocument = useCallback(
    async (
      document: TDocument,
      mode: 'reuse' | 'acquire' | 'refresh',
      nextZoomLevel: number,
      preserveCurrentPage: boolean
    ) => {
      setViewerStatus('loading')
      setViewerLoading(true)
      setViewerError(null)
      setViewerContentType(null)

      try {
        const sessionToken = await resolvePreviewToken(mode)
        const result = await mergedOptions.loadDocument(sessionToken, document)
        const resolvedKind = resolveSecureDocumentKind(document, result.contentType)
        const watermarkText = resolveWatermarkText(document)
        setViewerContentType(result.contentType ?? null)

        if (resolvedKind === 'unsupported') {
          setPdfSourceBlob(null)
          setViewerPdfPages([])
          revokeViewerObjectUrl()
          setViewerStatus('unavailable')
          setViewerError('Preview unavailable')
          return
        }

        if (resolvedKind === 'pdf') {
          setPdfSourceBlob(result.blob)
          revokeViewerObjectUrl()
          await renderPdfPages(result.blob, nextZoomLevel, preserveCurrentPage, watermarkText)
          return
        }

        setPdfSourceBlob(null)
        setViewerPdfPages([])
        const previewBlob = await renderImageWithWatermark(
          result.blob,
          watermarkText,
          result.contentType
        )
        const objectUrl = URL.createObjectURL(previewBlob)
        setViewerObjectUrl((currentValue) => {
          if (currentValue) {
            URL.revokeObjectURL(currentValue)
          }

          return objectUrl
        })
        setViewerStatus('ready')
      } catch (error) {
        if (readErrorCode(error) === PREVIEW_SESSION_EXPIRED_CODE) {
          setViewerStatus('expired')
          setViewerError('Session expired, Reload preview')
        } else {
          setViewerStatus('unavailable')
          setViewerError(readErrorMessage(error) ?? 'Preview unavailable')
        }
      } finally {
        setViewerLoading(false)
      }
    },
    [
      mergedOptions,
      renderPdfPages,
      resolvePreviewToken,
      resolveWatermarkText,
      revokeViewerObjectUrl,
    ]
  )

  const closeViewer = useCallback(() => {
    renderRequestIdRef.current += 1
    revokeViewerObjectUrl()
    setViewerDocument(null)
    setViewerContentType(null)
    setViewerPdfPages([])
    setViewerError(null)
    setViewerLoading(false)
    setViewerStatus('idle')
    setZoomLevel(1)
    setPdfSourceBlob(null)
    setCurrentPageIndex(0)

    const opener = openerElementRef.current
    if (opener && typeof opener.focus === 'function') {
      window.setTimeout(() => opener.focus(), 0)
    }
  }, [revokeViewerObjectUrl])

  const openViewer = useCallback(
    async (document: TDocument) => {
      if (!document.documentId) {
        setViewerError('Document cannot be viewed because its identifier is missing.')
        setViewerStatus('unavailable')
        return
      }

      if (typeof window !== 'undefined') {
        const activeElement = window.document.activeElement
        if (activeElement instanceof HTMLElement) {
          openerElementRef.current = activeElement
        }
      }

      renderRequestIdRef.current += 1
      revokeViewerObjectUrl()
      setViewerDocument(document)
      setViewerPdfPages([])
      setCurrentPageIndex(0)
      setZoomLevel(1)
      setPdfSourceBlob(null)

      await loadPreviewDocument(document, previewToken ? 'reuse' : 'acquire', 1, false)
    },
    [loadPreviewDocument, previewToken, revokeViewerObjectUrl]
  )

  const retryPreview = useCallback(async () => {
    if (!viewerDocument) {
      return
    }

    await loadPreviewDocument(viewerDocument, previewToken ? 'reuse' : 'acquire', zoomLevel, true)
  }, [loadPreviewDocument, previewToken, viewerDocument, zoomLevel])

  const reloadPreview = useCallback(async () => {
    if (!viewerDocument) {
      return
    }

    await loadPreviewDocument(viewerDocument, 'refresh', zoomLevel, true)
  }, [loadPreviewDocument, viewerDocument, zoomLevel])

  const setZoom = useCallback(
    async (nextZoomLevel: number, preserveCurrentPage: boolean) => {
      const clampedZoomLevel = clamp(nextZoomLevel, mergedOptions.minZoom, mergedOptions.maxZoom)
      setZoomLevel(clampedZoomLevel)
      if (
        viewerDocumentKind === 'pdf' &&
        pdfSourceBlob &&
        viewerStatus === 'ready' &&
        viewerDocument
      ) {
        await renderPdfPages(
          pdfSourceBlob,
          clampedZoomLevel,
          preserveCurrentPage,
          resolveWatermarkText(viewerDocument)
        )
      }
    },
    [
      mergedOptions.maxZoom,
      mergedOptions.minZoom,
      pdfSourceBlob,
      renderPdfPages,
      resolveWatermarkText,
      viewerDocument,
      viewerDocumentKind,
      viewerStatus,
    ]
  )

  const zoomIn = useCallback(async () => {
    await setZoom(zoomLevel + mergedOptions.zoomStep, true)
  }, [mergedOptions.zoomStep, setZoom, zoomLevel])

  const zoomOut = useCallback(async () => {
    await setZoom(zoomLevel - mergedOptions.zoomStep, true)
  }, [mergedOptions.zoomStep, setZoom, zoomLevel])

  const fitPreview = useCallback(
    async (fitBounds?: SecureViewerFitBounds) => {
      if (fitBounds) {
        const {viewportWidth, viewportHeight, contentWidth, contentHeight} = fitBounds
        if (viewportWidth > 0 && viewportHeight > 0 && contentWidth > 0 && contentHeight > 0) {
          const fitRatio = Math.min(viewportWidth / contentWidth, viewportHeight / contentHeight)
          await setZoom(clamp(fitRatio, mergedOptions.minZoom, mergedOptions.maxZoom), true)
          return
        }
      }

      await setZoom(1, true)
    },
    [mergedOptions.maxZoom, mergedOptions.minZoom, setZoom]
  )

  const resetPreview = useCallback(async () => {
    setCurrentPageIndex(0)
    await setZoom(1, false)
  }, [setZoom])

  const goToPreviousPage = useCallback(() => {
    setCurrentPageIndex((previousIndex) => Math.max(0, previousIndex - 1))
  }, [])

  const goToNextPage = useCallback(() => {
    setCurrentPageIndex((previousIndex) => Math.min(viewerPdfPages.length - 1, previousIndex + 1))
  }, [viewerPdfPages.length])

  const viewerSrc = useMemo(() => viewerObjectUrl ?? null, [viewerObjectUrl])
  const pageCount = viewerPdfPages.length
  const canGoToPreviousPage = viewerDocumentKind === 'pdf' && pageCount > 0 && currentPageIndex > 0
  const canGoToNextPage =
    viewerDocumentKind === 'pdf' && pageCount > 0 && currentPageIndex < pageCount - 1
  const canZoomOut = zoomLevel > mergedOptions.minZoom
  const canZoomIn = zoomLevel < mergedOptions.maxZoom

  return {
    viewerDocument,
    viewerDocumentKind,
    viewerSrc,
    viewerPdfPages,
    viewerLoading,
    viewerError,
    viewerStatus,
    zoomLevel,
    zoomPercent: Math.round(zoomLevel * 100),
    currentPageIndex,
    pageCount,
    canGoToPreviousPage,
    canGoToNextPage,
    canZoomOut,
    canZoomIn,
    openViewer,
    retryPreview,
    reloadPreview,
    zoomIn,
    zoomOut,
    fitPreview,
    resetPreview,
    goToPreviousPage,
    goToNextPage,
    closeViewer,
  }
}
